/**
 * Confirmation dialog for releasing an investment item / budget detail.
 *
 * Shows the user *exactly* what's about to happen: the line title, source
 * pot, total budget, used % (preserved as historical stat), and the amount
 * being returned to the bucket. Also captures an optional reason that ends
 * up in the audit log next to the RELEASE bucket transaction.
 *
 * Once released, the line is locked: no further edits, no project-part
 * assignments, no deletion. Stats remain visible.
 */

import { useEffect, useState } from "react";
import { Lock, Wallet, Warning } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { InvestmentItem } from "screens/investment/types/investment";

interface ReleaseInvestmentItemDialogProps {
  item: InvestmentItem | null;
  isOpen: boolean;
  onClose: () => void;
  /** Called with the typed reason (or undefined). Returns when the BE call is
      done so the dialog can show a spinner. Errors are caught here and shown
      via toast in the caller. */
  onConfirm: (reason: string | undefined) => Promise<void>;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

export const ReleaseInvestmentItemDialog = ({
  item,
  isOpen,
  onClose,
  onConfirm,
}: ReleaseInvestmentItemDialogProps) => {
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset draft state every time the dialog opens with a fresh item.
  useEffect(() => {
    if (isOpen) setReason("");
  }, [isOpen, item?.id]);

  if (!item) return null;

  const total = Number(item.estimatedCostExclVat) || 0;
  const used = Number(item.allocatedBudget) || 0;
  const remainder = Math.max(0, total - used);
  const usedPct = total > 0 ? Math.round((used / total) * 100) : 0;
  const bucketName = item.bucket?.name ?? null;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(reason.trim() || undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-amber-600" weight="fill" />
            Vrijgeven — {item.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-neutral-secondary-mid">
            Het ongebruikte bedrag gaat terug naar het strategisch budget. Deze lijn wordt
            daarna vergrendeld voor verdere bewerkingen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Source pot */}
          <div className="flex items-center justify-between rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
            <div className="flex items-center gap-2 text-sm">
              <Wallet className="h-4 w-4 text-moss-primary" weight="duotone" />
              <span className="text-neutral-secondary-mid">Terug naar strategisch budget</span>
            </div>
            <span className="text-sm font-semibold text-neutral-primary">
              {bucketName ?? "Overflow (geen strategisch budget)"}
            </span>
          </div>

          {/* Numbers */}
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-md border border-neutral-200 bg-white p-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-secondary-mid">
                Origineel
              </div>
              <div className="mt-0.5 text-sm font-semibold tabular-nums text-neutral-primary">
                {formatCurrency(total)}
              </div>
            </div>
            <div className="rounded-md border border-neutral-200 bg-white p-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-secondary-mid">
                Gebruikt ({usedPct}%)
              </div>
              <div className="mt-0.5 text-sm font-semibold tabular-nums text-neutral-primary">
                {formatCurrency(used)}
              </div>
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                Vrij te geven
              </div>
              <div className="mt-0.5 text-sm font-bold tabular-nums text-amber-800">
                {formatCurrency(remainder)}
              </div>
            </div>
          </div>

          {/* Usage bar — visualises the gebruikt vs. vrij te geven split */}
          <div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
              <div
                className="h-full bg-moss-primary transition-all"
                style={{ width: `${Math.min(100, usedPct)}%` }}
              />
            </div>
            <div className="mt-1 flex justify-between text-[10px] text-neutral-secondary-mid">
              <span>{formatCurrency(used)} gebruikt</span>
              <span>{formatCurrency(remainder)} vrij te geven</span>
            </div>
          </div>

          {/* Lockdown notice */}
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" weight="fill" />
            <div>
              Na het vrijgeven kan deze budgetlijn niet meer worden gewijzigd of
              aan projectonderdelen worden gekoppeld. De statistieken blijven
              wel zichtbaar in de lijst.
            </div>
          </div>

          {remainder === 0 && (
            <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              <Warning className="mt-0.5 h-4 w-4 shrink-0" weight="fill" />
              Deze lijn is volledig benut — er is niets meer terug te storten.
              De lijn wordt enkel vergrendeld.
            </div>
          )}

          {/* Optional reason */}
          <div>
            <label
              htmlFor="release-reason"
              className="mb-1 block text-xs font-medium text-neutral-secondary-mid"
            >
              Reden (optioneel) — verschijnt in de audit log
            </label>
            <Textarea
              id="release-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Bijv. budgetlijn heeft zijn doel bereikt, project geannuleerd, scope verkleind, ..."
              rows={2}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Annuleren
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="bg-amber-600 text-white hover:bg-amber-700"
          >
            <Lock className="mr-1 h-4 w-4" weight="fill" />
            {isSubmitting ? "Vrijgeven…" : `Vrijgeven (${formatCurrency(remainder)})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReleaseInvestmentItemDialog;
